// Module: builderTreeModel.js
// Purpose: Pure derived Strategy Builder tree model (Entry/Exit/Risk/Chart/Not used) built from nodes, edges and readiness items.

import { CHART_VISUAL_TYPES, getNodeAccent, TRIGGER_NODE_TYPES } from '../config/nodeVisualConfig';
import {
    barsAgoTag,
    collectTriggerTree,
    COMPARATOR_OPS,
    comparatorOperatorText,
    dataSourceTitle,
    flattenTriggerTree,
    formatWindowBound,
    gateOperator,
    resolveAuthoredName,
    isModelNode,
    resolveDisplayName,
    resolveNameDetail,
    resolveOperandLabel,
    resolveOperandName,
    resolveOperandSegmentText,
} from './nodeUtils';
import {
    matchesTargetInput,
    resolveConditionAttachHandle,
    resolveTargetHandle,
    targetInputKind,
    targetInputSlots,
} from './operandLinking';

const SECTION_DEFINITIONS = [
    { id: 'entry', label: 'Entry' },
    { id: 'exit', label: 'Exit' },
    { id: 'risk', label: 'Risk' },
    { id: 'chart', label: 'Chart' },
    { id: 'not-used', label: 'Not used' },
];

const ENTRY_ACTIONS = new Set(['enter_long', 'enter_short', 'buy', 'sell']);
const EXIT_ACTIONS = new Set(['exit_long', 'exit_short']);
const RISK_ACTIONS = new Set(['sl', 'tp', 'time_exit']);
const CHART_ROOT_TYPES = new Set([...CHART_VISUAL_TYPES, 'chart_output', 'chart_node']);

const triggerSection = (node) => {
    const action = String(node?.data?.action || '').toLowerCase();
    if (ENTRY_ACTIONS.has(action)) return 'entry';
    if (EXIT_ACTIONS.has(action)) return 'exit';
    if (RISK_ACTIONS.has(action)) return 'risk';
    return null;
};

const rowKind = (node, isRoot = false) => {
    if (TRIGGER_NODE_TYPES.includes(node?.type)) return 'trigger';
    if (CHART_ROOT_TYPES.has(node?.type)) return 'chart';
    if (node?.type === 'logic_gate') return 'group';
    if (node?.type === 'comparator' || node?.type === 'time_window') return 'condition';
    if (isRoot) return 'operand';
    return 'node';
};

const operandKind = (node, input) => {
    if (isModelNode(node)) return 'model';
    if (node?.type === 'indicator') return 'indicator';
    if (node?.type === 'data_source') return 'price';
    if (node?.type === 'math') return 'math';
    if (node?.type === 'constant' || input?.type === 'constant') return 'constant';
    return node?.type || input?.type || 'operand';
};

//--> detail text for an operand: its node's own scalar params ('period 20 · source close'), else the price field
const operandParamsText = (node, field) => {
    const params = node?.data?.params && typeof node.data.params === 'object' ? node.data.params : {};
    const bits = Object.entries(params)
        .filter(([, value]) => value != null && value !== '' && typeof value !== 'object')
        .map(([key, value]) => `${key} ${value}`);
    if (bits.length === 0 && node?.type === 'data_source' && field) bits.push(`field ${field}`);
    return bits.join(' · ');
};

//--> group summary on the row line: 'all of N' / 'any of N' / 'inverts its input' (N = the group's inputs shown)
const groupSummary = (node, count) => {
    const op = gateOperator(node);
    if (op === 'NOT') return 'inverts its input';
    if (op === 'AND') return `all of ${count}`;
    if (op === 'OR') return `any of ${count}`;
    return null;
};

/*>>> RULE TEXT (detail block 'Rule' line) <<<*/
//--> the condition in plain words, built from the same chip names the row line shows
const operandPhrase = (chip) => {
    if (!chip || (!chip.nodeId && chip.kind !== 'constant')) return '(not connected)';
    const name = chip.name ?? chip.label;
    return chip.barsAgoTag ? `${name} ${chip.barsAgoTag}` : name;
};
const operatorWords = (operator) => {
    const op = COMPARATOR_OPS[operator];
    if (!op) return operator || '?';
    if (String(operator).startsWith('crosses_') || operator === 'eq') return op.words;
    return `is ${op.words}`;
};
//--> the subject phrase a parent trigger reads ('fires when <phrase>')
const conditionPhrase = (row) => {
    if (row.node?.type === 'comparator') {
        const [left, ...rest] = row.inputSlots;
        if (!left) return `${row.label} is true`;
        return [operandPhrase(left.chip), operatorWords(row.node.data?.operator), ...rest.map(({ chip }) => operandPhrase(chip))].join(' ');
    }
    if (row.node?.type === 'time_window') {
        return `time is between ${operandPhrase(row.inputSlots[0]?.chip)} and ${operandPhrase(row.inputSlots[1]?.chip)} IST`;
    }
    if (row.node?.type === 'logic_gate') return `${row.name || `the ${row.label} group`} is true`;
    return `${row.label} is true`;
};
const ruleTextFor = (row, count) => {
    const type = row.node?.type;
    if (type === 'comparator') return conditionPhrase(row);
    if (type === 'time_window') {
        return `true between ${operandPhrase(row.inputSlots[0]?.chip)} and ${operandPhrase(row.inputSlots[1]?.chip)} IST`;
    }
    if (type === 'logic_gate') {
        if (row.gateOp === 'NOT') return 'inverts its input';
        if (row.gateOp === 'AND') return `true when all of ${count} input${count === 1 ? ' is' : 's are'} true`;
        if (row.gateOp === 'OR') return `true when any of ${count} input${count === 1 ? ' is' : 's are'} true`;
        return null;
    }
    //* entry/exit signals fire when ALL their inputs are true (graph_evaluator _eval_signal_node); risk triggers
    //  (SL/TP/time exit) are price/time driven with association inputs, so they get no Rule line
    if (row.kind === 'trigger' && ['entry', 'exit'].includes(triggerSection(row.node))) {
        if (row.children.length === 0) return 'no input connected';
        if (row.children.length > 1) return `fires when all of ${row.children.length} inputs are true`;
        return `fires when ${conditionPhrase(row.children[0])}`;
    }
    return null;
};

const labelsByOffendingId = (readinessItems) => {
    const flags = {};
    (readinessItems || []).forEach((item) => {
        const label = item?.label == null ? 'Readiness issue' : String(item.label);
        (item?.offending_node_ids || []).forEach((nodeId) => {
            if (!nodeId) return;
            if (!flags[nodeId]) flags[nodeId] = [];
            flags[nodeId].push(label);
        });
    });
    return flags;
};

const usageBySource = (nodes, edges) => {
    const consumers = new Map((nodes || []).map(({ id }) => [id, new Set()]));
    (edges || []).forEach(({ source, target }) => {
        if (consumers.has(source) && target != null) consumers.get(source).add(target);
    });
    return Object.fromEntries([...consumers].map(([id, targets]) => [id, targets.size]));
};

const inboundIndex = (edges) => {
    const inbound = new Map();
    (edges || []).forEach((candidate) => {
        if (!inbound.has(candidate.target)) inbound.set(candidate.target, []);
        inbound.get(candidate.target).push(candidate);
    });
    return inbound;
};

const inboundReachability = (seedIds, inbound) => {
    const reached = new Set();
    const pending = [...seedIds];
    while (pending.length > 0) {
        const id = pending.pop();
        if (reached.has(id)) continue;
        reached.add(id);
        (inbound.get(id) || []).forEach(({ source }) => {
            if (!reached.has(source)) pending.push(source);
        });
    }
    return reached;
};

//--> The ONE authority for the sourceHandle a Tree wire carries when there is no edge being moved: the node's existing
//    outgoing edge's own sourceHandle (exact copy of what the Canvas stored), else the named output handle the
//    node renders (DataSourceNode 'close', SignalNode 'value'), else null — React Flow stores null for an unnamed
//    source handle, so a Tree wire saves exactly what a Canvas wire saves (ARB-1).
const NAMED_SOURCE_HANDLES = { data_source: 'close', signal: 'value' };
export const treeSourceHandle = (node, edges = []) => {
    const outgoing = (edges || []).find((candidate) => candidate.source === node?.id);
    if (outgoing) return outgoing.sourceHandle ?? null;
    return NAMED_SOURCE_HANDLES[node?.type] ?? null;
};

//--> Drag source for a tree payload: an edge being moved carries its own sourceHandle verbatim; otherwise the
//    treeSourceHandle authority. Never coerced to 'value'.
const dragSourceFor = (node, edge, edges) => ({
    nodeId: node.id,
    sourceHandle: edge ? edge.sourceHandle : treeSourceHandle(node, edges),
});

const incomingEdgeIds = (edges, source, target) => (
    (edges || [])
        .filter((candidate) => candidate.source === source && candidate.target === target)
        .map(({ id }) => id)
        .filter(Boolean)
        .sort()
);

const findInputEdge = (node, input, index, edges) => {
    return (edges || []).find((candidate) => (
        matchesTargetInput(candidate, node.id, input?.id, index)
    ));
};

const upstreamOperands = (node, edges, byId) => {
    return (edges || [])
        .filter(({ target }) => target === node?.id)
        .sort((left, right) => (
            String(left.targetHandle || '').localeCompare(String(right.targetHandle || ''))
            || String(left.id || '').localeCompare(String(right.id || ''))
        ))
        .map((edge) => ({ node: byId.get(edge.source), field: edge.sourceHandle || null }))
        .filter(({ node: sourceNode }) => sourceNode);
};

const buildSegments = ({ sourceNode, field, nodes, edges, byId, visualOverrides, ancestorTrail, rowKey }) => {
    if (!sourceNode) return [];
    const segments = [];
    const emitted = new Set();
    const visit = (current, currentField, downstreamTrail, path) => {
        if (!current || path.has(current.id)) return;
        const nextPath = new Set(path);
        nextPath.add(current.id);
        if (current.type === 'math' || current.type === 'indicator') {
            const nextTrail = [{ nodeId: current.id, rowKey }, ...downstreamTrail];
            upstreamOperands(current, edges, byId).forEach((upstream) => {
                visit(upstream.node, upstream.field, nextTrail, nextPath);
            });
        }
        if (emitted.has(current.id)) return;
        emitted.add(current.id);
        const segmentText = resolveOperandSegmentText(
            current,
            currentField,
            nodes,
            edges,
            visualOverrides?.[current.id],
            visualOverrides,
        );
        segments.push({
            nodeId: current.id,
            type: current.type,
            data: current.data || {},
            label: segmentText.primary,
            ...(segmentText.authoredName ? { authoredName: segmentText.authoredName } : {}),
            ...(current.type === 'data_source' ? {
                sourceTitle: dataSourceTitle(current),
                detail: [dataSourceTitle(current), current.data?.timeframe].filter(Boolean).join(' '),
            } : {}),
            field: currentField || null,
            accent: getNodeAccent(current.type, current.data || {}),
            ancestorTrail: downstreamTrail,
        });
    };
    visit(sourceNode, field, ancestorTrail, new Set());
    return segments;
};

const buildChip = ({
    node, input, index, nodes, edges, byId, usageById, flagsById, visualOverrides, ancestorTrail, rowKey,
}) => {
    const inputEdge = findInputEdge(node, input, index, edges);
    const sourceId = inputEdge?.source || input?.nodeId || null;
    const sourceNode = byId.get(sourceId);
    const kind = operandKind(sourceNode, input);
    const lookback = barsAgoTag(input);
    const chip = {
        kind,
        nodeId: sourceId,
        node: sourceNode || null,
        field: input?.field || inputEdge?.sourceHandle || null,
        label: resolveOperandLabel(node, index, nodes, edges, 0, visualOverrides || {}),
        name: resolveOperandName(node, index, nodes, edges, 0, visualOverrides || {}),
        barsAgoTag: lookback,
        paramsText: operandParamsText(sourceNode, input?.field || inputEdge?.sourceHandle),
        usageCount: sourceId ? (usageById[sourceId] || 0) : 0,
        readinessLabels: sourceId ? (flagsById[sourceId] || []) : [],
        connectable: sourceNode?.connectable,
        deletable: sourceNode?.deletable,
        ancestorTrail,
        segments: buildSegments({
            sourceNode,
            field: input?.field || inputEdge?.sourceHandle || null,
            nodes,
            edges,
            byId,
            visualOverrides,
            ancestorTrail,
            rowKey,
        }),
    };
    if (sourceNode && kind !== 'constant') {
        chip.dragSource = dragSourceFor(sourceNode, inputEdge, edges);
    }
    return chip;
};

//--> ONE slots array per row: the operand-linking authority classifies boolean vs numeric inputs and
//    supplies factory-compatible defaults. A time window uses display-only bounds of its own node.
const buildInputSlots = (node, context, occurrence) => {
    const slotTrail = [{ nodeId: node.id, rowKey: occurrence.rowKey }, ...occurrence.ancestorTrail];
    if (node?.type === 'time_window') {
        return ['from', 'to'].map((field) => ({
            side: field,
            handleId: field,
            chip: {
                kind: 'constant',
                nodeId: node.id,
                node,
                field,
                label: formatWindowBound(node.data?.[field]),
                name: formatWindowBound(node.data?.[field]),
                barsAgoTag: '',
                paramsText: '',
                usageCount: context.usageById[node.id] || 0,
                readinessLabels: context.flagsById[node.id] || [],
                connectable: node.connectable,
                deletable: node.deletable,
                ancestorTrail: occurrence.ancestorTrail,
            },
            dropTarget: null,
            ancestorTrail: slotTrail,
        }));
    }
    const inputKind = targetInputKind(node);
    if (!inputKind) return [];
    const inputs = targetInputSlots(node);
    return inputs.map((input, index) => {
        const handleId = resolveTargetHandle(node, input?.id, index);
        return {
            side: inputKind === 'operand'
                ? (index === 0 ? 'left' : index === 1 ? 'right' : `input-${index + 1}`)
                : (inputs.length === 1 ? 'condition' : `condition-${index + 1}`),
            handleId,
            inputKind,
            chip: buildChip({
                node, input, index, ...context, ancestorTrail: slotTrail, rowKey: occurrence.rowKey,
            }),
            dropTarget: { target: node.id, targetHandle: handleId },
            ancestorTrail: slotTrail,
        };
    });
};


/**
 * Build the author-facing Strategy Builder tree as a pure projection of the
 * serialized graph and the readiness items already owned by StrategyBuilder.
 * `visualOverrides` is the display record's node-id -> entry map (50-21): custom names / notes
 * come ONLY from it, through the one `resolveDisplayName` (no group-label rule of its own).
 */
export const buildBuilderTreeModel = (nodes = [], edges = [], readinessItems = [], visualOverrides = {}) => {
    const safeNodes = Array.isArray(nodes) ? nodes : [];
    const safeEdges = Array.isArray(edges) ? edges : [];
    const byId = new Map(safeNodes.map((candidate) => [candidate.id, candidate]));
    const nodesById = Object.fromEntries(safeNodes.map((candidate) => [candidate.id, candidate]));
    const flagsById = labelsByOffendingId(readinessItems);
    const usageById = usageBySource(safeNodes, safeEdges);
    const inbound = inboundIndex(safeEdges);
    const triggerNodes = safeNodes.filter((candidate) => TRIGGER_NODE_TYPES.includes(candidate.type));
    const chartNodes = safeNodes.filter((candidate) => CHART_ROOT_TYPES.has(candidate.type));
    const triggerTrees = new Map(triggerNodes.map((candidate) => [
        candidate.id,
        collectTriggerTree(candidate.id, safeNodes, safeEdges),
    ]));

    const sharedSets = new Map();
    triggerNodes.forEach((trigger) => {
        flattenTriggerTree(triggerTrees.get(trigger.id)).forEach((nodeId) => {
            if (!sharedSets.has(nodeId)) sharedSets.set(nodeId, new Set());
            sharedSets.get(nodeId).add(trigger.id);
        });
    });
    const sharedById = Object.fromEntries(
        [...sharedSets].map(([nodeId, triggerIds]) => [nodeId, [...triggerIds]]),
    );

    const seedIds = [...triggerNodes, ...chartNodes].map(({ id }) => id);
    const reached = inboundReachability(seedIds, inbound);
    const unsupportedTriggers = triggerNodes.filter((candidate) => triggerSection(candidate) === null);
    const unusedNodes = safeNodes.filter((candidate) => !reached.has(candidate.id));

    const sectionRoots = {
        entry: triggerNodes.filter((candidate) => triggerSection(candidate) === 'entry'),
        exit: triggerNodes.filter((candidate) => triggerSection(candidate) === 'exit'),
        risk: triggerNodes.filter((candidate) => triggerSection(candidate) === 'risk'),
        chart: chartNodes,
        'not-used': [...unsupportedTriggers, ...unusedNodes],
    };

    const claimed = new Set();
    const ownerById = {};
    const context = { nodes: safeNodes, edges: safeEdges, byId, usageById, flagsById, visualOverrides };
    const mathOperandTrees = (node, path = new Set()) => {
        const emitted = new Set();
        return upstreamOperands(node, safeEdges, byId).flatMap(({ node: sourceNode }) => {
            if (sourceNode?.type !== 'math' || path.has(sourceNode.id) || emitted.has(sourceNode.id)) return [];
            emitted.add(sourceNode.id);
            const nextPath = new Set(path);
            nextPath.add(sourceNode.id);
            return [{ node: sourceNode, children: mathOperandTrees(sourceNode, nextPath) }];
        });
    };

    const makeRow = ({
        tree, sectionId, rootNode, ownerTriggerId, parentId = null, level = 1, path = [], ancestorTrail = [],
    }) => {
        const current = tree?.node || rootNode;
        const isRoot = parentId === null;
        const key = [sectionId, rootNode.id, ...path, current.id].join(':');
        const canReference = !isRoot && claimed.has(current.id);
        const ownerId = ownerById[current.id] || ownerTriggerId;
        const ownerNode = byId.get(ownerId);
        const readinessLabels = flagsById[current.id] || [];
        const recordEntry = visualOverrides?.[current.id];
        const directChildren = tree?.children || [];
        const directChildIds = new Set(directChildren.map((child) => child.node?.id));
        const mathChildren = mathOperandTrees(current, new Set([...path, current.id]))
            .filter((child) => !directChildIds.has(child.node.id));
        const childTrees = [...directChildren, ...mathChildren];
        const childCount = childTrees.length;
        const base = {
            key,
            occurrenceKey: key,
            kind: canReference ? 'reference' : rowKind(current, isRoot),
            isReference: canReference,
            nodeId: current.id,
            node: current,
            label: resolveDisplayName(current, safeNodes, safeEdges, recordEntry, visualOverrides),
            //! the CURRENT auto-assembled logic text, only while a record custom_name is set (detail line)
            ...resolveNameDetail(current, safeNodes, safeEdges, recordEntry, visualOverrides),
            level,
            unused: sectionId === 'not-used',
            ownerTriggerId: ownerId,
            incomingEdgeIds: parentId ? incomingEdgeIds(safeEdges, current.id, parentId) : [],
            readinessLabels,
            flagText: readinessLabels.join(', '),
            usageCount: usageById[current.id] || 0,
            connectable: current.connectable,
            deletable: current.deletable,
            children: [],
            inputSlots: [],
            conditionAttachTarget: null,
            ancestorTrail,
        };
        //--> row-line sentence parts (the renderer only lays them out): operator, group pill/name/summary, own chip
        if (current.type === 'comparator') base.operatorText = comparatorOperatorText(current.data?.operator);
        //--> 50-21 Live-Val: an authored name is the row's primary text on conditions too (sentence chips go to the detail)
        if (current.type === 'comparator' || current.type === 'time_window') {
            const authored = resolveAuthoredName(current, recordEntry);
            if (authored) base.name = authored;
        }
        if (current.type === 'logic_gate') {
            base.gateOp = gateOperator(current);
            base.name = resolveAuthoredName(current, recordEntry);
            base.summaryText = groupSummary(current, childCount);
        }
        if (['operand', 'node'].includes(rowKind(current, isRoot))) {
            base.selfChip = {
                kind: operandKind(current),
                nodeId: current.id,
                node: current,
                name: base.label,
                label: base.label,
                barsAgoTag: '',
                usageCount: base.usageCount,
                readinessLabels: [],
                ancestorTrail,
            };
        }
        //--> a child occurrence (full copy or reference) moves its own parent edge, carrying that edge's handle
        if (parentId && base.incomingEdgeIds.length > 0) {
            const parentEdge = safeEdges.find(({ id }) => id === base.incomingEdgeIds[0]);
            base.dragSource = dragSourceFor(current, parentEdge, safeEdges);
        }

        if (canReference) {
            base.referenceText = `same as in ${resolveDisplayName(ownerNode, safeNodes, safeEdges, visualOverrides?.[ownerNode?.id], visualOverrides)}`;
            //--> the same sentence as the full row, display only: a reference is never a drop target
            base.inputSlots = buildInputSlots(current, context, { rowKey: key, ancestorTrail })
                .map((slot) => ({ ...slot, dropTarget: null }));
            base.ruleText = ruleTextFor(base, childCount);
            return base;
        }

        claimed.add(current.id);
        if (!ownerById[current.id]) ownerById[current.id] = ownerTriggerId;
        base.inputSlots = buildInputSlots(current, context, { rowKey: key, ancestorTrail });
        const conditionHandle = targetInputKind(current) === 'boolean'
            ? resolveConditionAttachHandle(current, safeEdges)
            : null;
        if (current.connectable !== false
            && conditionHandle !== null && conditionHandle !== undefined && conditionHandle !== '') {
            base.conditionAttachTarget = { nodeId: current.id, targetHandle: conditionHandle };
        }
        //--> a Not-used root (operand, condition or group) is a wire-in source; triggers and charts never drag
        if (isRoot && ['operand', 'condition', 'group'].includes(rowKind(current, true))
            && operandKind(current) !== 'constant') {
            base.dragSource = dragSourceFor(current, null, safeEdges);
        }
        base.children = childTrees.map((child) => makeRow({
            tree: child,
            sectionId,
            rootNode,
            ownerTriggerId,
            parentId: current.id,
            level: level + 1,
            path: [...path, current.id],
            ancestorTrail: [{ nodeId: current.id, rowKey: key }, ...ancestorTrail],
        }));
        base.ruleText = ruleTextFor(base, childCount);
        return base;
    };

    const sections = SECTION_DEFINITIONS.map((definition) => {
        const roots = sectionRoots[definition.id].map((rootNode) => {
            const tree = triggerTrees.get(rootNode.id)
                || (definition.id === 'chart' ? collectTriggerTree(rootNode.id, safeNodes, safeEdges) : { node: rootNode, children: [] });
            return makeRow({
                tree,
                sectionId: definition.id,
                rootNode,
                ownerTriggerId: rootNode.id,
            });
        });
        const rows = [];
        const flatten = (row) => {
            rows.push(row);
            row.children.forEach(flatten);
        };
        roots.forEach(flatten);
        return { ...definition, roots, rows };
    });

    return {
        sections,
        nodesById,
        edges: safeEdges,
        flagsById,
        usageById,
        sharedById,
        ownerById,
        reachedNodeIds: safeNodes.filter(({ id }) => reached.has(id)).map(({ id }) => id),
        unusedNodeIds: unusedNodes.map(({ id }) => id),
    };
};

export default buildBuilderTreeModel;
